// `signatures` — the array of signature events for a report. Stored as
// JSONB on `reports.signatures`. Always paired with `parties`: a signature
// at role `arrendatario` only makes sense if `parties` has a row at that
// role too.
//
// `signatureData` is the data URL of a hand-drawn signature (PNG base64
// from SignatureCanvas). We keep it bounded to stay sane on row size — the
// signed report is the source of truth, not the JSONB blob.
//
// `declineNote` (ADR-0028) — optional free text the signer attaches when
// declining. Used by facility's constancia path when motivo='otro' to
// capture the specific reason. UI surfaces a warning against
// special-category data (Ley 21.719 Art. 13).
import { isoDateTimeSchema, slugSchema } from "@bitacorapro/shared";
import { z } from "zod";
const SIG_DATA_URL = /^data:image\/(png|svg\+xml);base64,[A-Za-z0-9+/=]+$/;
const signatureSchema = z
    .object({
    role: slugSchema,
    signedAt: isoDateTimeSchema,
    signatureData: z
        .string()
        .max(200_000)
        .regex(SIG_DATA_URL, { message: "signatureData must be a base64 data URL" })
        .optional(),
    declined: z.boolean().default(false),
    /** Optional free text accompanying a declined signature. Bounded to
     *  240 chars. When declining with motivo='otro' under a recipe with
     *  `declineReasonCapture: "enum_plus_optional_free_text"`, the
     *  engine requires this to be non-empty (validated in invariants
     *  validateConstancia). */
    declineNote: z.string().min(1).max(240).optional(),
})
    .superRefine((sig, ctx) => {
    // A signature is either provided OR explicitly declined; never both,
    // never neither. This is the rule that makes the signed-but-refused
    // case unambiguous in the audit trail.
    const hasData = sig.signatureData !== undefined;
    if (hasData && sig.declined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["declined"],
            message: "a declined signature must not include signatureData",
        });
    }
    if (!hasData && !sig.declined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "signature must include signatureData or declined=true",
        });
    }
    if (sig.declineNote !== undefined && !sig.declined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["declineNote"],
            message: "declineNote only valid on declined signatures",
        });
    }
});
export const signaturesSchema = z.array(signatureSchema);
// Constancia "no firmó" report-level payload (ADR-0028 / H-03).
// Mirrors the three top-level columns on the `reports` table:
//   - constancia_no_firma_motivo  (enum)
//   - constancia_no_firma_role    (declined signer's role)
//   - constancia_no_firma_at      (timestamp)
//
// The optional free text under motivo='otro' lives on the signature
// itself (signature.declineNote) rather than as a fourth report column —
// keeps the report-level columns to three (ADR-0028) and puts the
// signer-attributable note next to the signature event.
export const constanciaMotivoSchema = z.enum([
    "no_disponible",
    "ausencia_justificada",
    "se_nego_a_firmar",
    "fuerza_mayor",
    "otro",
]);
export const constanciaSchema = z.object({
    motivo: constanciaMotivoSchema,
    /** slug of the declined signer's role (must match a signatureRoles[].partyRole
     *  whose declinable is true). */
    role: slugSchema,
    at: isoDateTimeSchema,
});
//# sourceMappingURL=signatures.js.map